/**
 * Per-article sourcing-insight card. One instance per SourcingArticleAnalysis.
 *
 * Visual hierarchy (collapsed → expanded):
 *   headline → chips (sentiment/impact/horizon) → summary →
 *   meta row (source · date · domain link) → expand control →
 *   [signal definition list → analysis → recommendations → keywords]
 */

import { memo, useMemo, useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import type {
  CategoryImpact,
  DirectionOfImpact,
  Magnitude,
  SourcingArticleAnalysis,
  SourcingSentiment,
  TimeHorizon,
} from './types';

interface SourcingInsightCardProps {
  article: SourcingArticleAnalysis;
}

// ─── Formatting helpers ────────────────────────────────────────────────────

function titleCase(snake: string): string {
  return snake
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function domainFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const host = new URL(url).hostname;
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return null;
  }
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return value;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}`;
}

// ─── Tone maps ─────────────────────────────────────────────────────────────

const SENTIMENT_TONE: Record<SourcingSentiment, string> = {
  bullish: 'bg-emerald-50 text-emerald-700',
  bearish: 'bg-rose-50 text-rose-700',
  neutral: 'bg-neutral-100 text-neutral-700',
  cautious: 'bg-amber-50 text-amber-700',
};

const IMPACT_TONE: Record<Magnitude, string> = {
  high: 'bg-rose-50 text-rose-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-neutral-100 text-neutral-600',
};

const HORIZON_TONE: Record<TimeHorizon, string> = {
  immediate: 'bg-rose-50 text-rose-700',
  short: 'bg-amber-50 text-amber-700',
  medium: 'bg-sky-50 text-sky-700',
  long: 'bg-indigo-50 text-indigo-700',
};

const CATEGORY_TONE: Record<CategoryImpact, string> = {
  supply: 'bg-amber-50 text-amber-700',
  demand: 'bg-blue-50 text-blue-700',
  geopolitical: 'bg-rose-50 text-rose-700',
  regulatory: 'bg-violet-50 text-violet-700',
  energy: 'bg-orange-50 text-orange-700',
  technology: 'bg-cyan-50 text-cyan-700',
};

const DIRECTION_TONE: Record<DirectionOfImpact, string> = {
  bullish: 'bg-emerald-50 text-emerald-700',
  bearish: 'bg-rose-50 text-rose-700',
  neutral: 'bg-neutral-100 text-neutral-600',
};

// ─── Sub-components ─────────────────────────────────────────────────────────

/** Standardised chip — same shape, size, weight everywhere. */
function Chip({ text, tone }: { text: string; tone: string }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone}`}
    >
      {text}
    </span>
  );
}

/** Consistent uppercase section label. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">
      {children}
    </p>
  );
}

// ─── Main card ──────────────────────────────────────────────────────────────

function SourcingInsightCardBase({ article }: SourcingInsightCardProps) {
  const [expanded, setExpanded] = useState(false);

  const domain = useMemo(() => domainFromUrl(article.article_url), [article.article_url]);
  const articleDate = useMemo(
    () => formatDate(article.article_published_date),
    [article.article_published_date],
  );
  const eventDate = useMemo(() => formatDate(article.date_of_event), [article.date_of_event]);

  // Skipped articles render a minimal row (defensive — typically filtered out upstream).
  if (article.status === 'skipped') {
    return (
      <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
        <p className="text-sm font-medium text-neutral-700">{article.article_title}</p>
        <p className="mt-1 text-xs text-neutral-500">
          {article.skip_reason ?? 'Skipped (no reason provided)'}
        </p>
      </div>
    );
  }

  const hasKeywords =
    article.related_commodities.length > 0 ||
    article.related_suppliers.length > 0 ||
    article.related_categories.length > 0;

  return (
    <article className="relative rounded-xl border border-neutral-200 bg-white">
      {/* corner link to source article */}
      {article.article_url && (
        <a
          href={article.article_url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute right-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-300 transition-colors hover:bg-neutral-50 hover:text-primary-600"
          aria-label="Open source article in a new tab"
          title="Open source article"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}

      {/* ── header section ── */}
      <div className="px-5 pb-4 pt-5">
        {article.headline && (
          <h3 className="pr-10 text-[15px] font-bold leading-snug text-neutral-900">
            {article.headline}
          </h3>
        )}

        {(article.sentiment || article.impact_level || article.time_horizon) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {article.sentiment && (
              <Chip text={article.sentiment} tone={SENTIMENT_TONE[article.sentiment]} />
            )}
            {article.impact_level && (
              <Chip
                text={`${article.impact_level} impact`}
                tone={IMPACT_TONE[article.impact_level]}
              />
            )}
            {article.time_horizon && (
              <Chip
                text={article.time_horizon}
                tone={HORIZON_TONE[article.time_horizon]}
              />
            )}
          </div>
        )}

        {article.summary && (
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            {article.summary}
          </p>
        )}
      </div>

      {/* ── meta + expand band ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 bg-neutral-50/40 px-5 py-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
          {article.article_source && (
            <span className="font-medium text-neutral-700">{article.article_source}</span>
          )}
          {articleDate && <span>{articleDate}</span>}
          {domain && article.article_url && (
            <a
              href={article.article_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-sky-700 transition-colors hover:text-sky-900"
              title={`Open ${domain} in a new tab`}
            >
              {domain}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
          aria-expanded={expanded}
        >
          {expanded ? 'Hide details' : 'Show details'}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* ── expanded body ── */}
      {expanded && (
        <div className="space-y-5 border-t border-neutral-100 px-5 py-5">
          {/* Event date — labeled value */}
          {eventDate && (
            <section>
              <SectionLabel>Date of Event</SectionLabel>
              <p className="mt-1 text-sm font-medium text-neutral-800">{eventDate}</p>
            </section>
          )}

          {/* Signal — inline chip row (category · direction · magnitude) */}
          {(article.category_impact || article.direction_of_impact || article.magnitude) && (
            <section className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {article.category_impact && (
                <Chip
                  text={article.category_impact}
                  tone={CATEGORY_TONE[article.category_impact]}
                />
              )}
              {article.direction_of_impact && (
                <Chip
                  text={article.direction_of_impact}
                  tone={DIRECTION_TONE[article.direction_of_impact]}
                />
              )}
              {article.magnitude && (
                <span className="text-xs text-neutral-700">
                  Magnitude:{' '}
                  <span className="font-bold text-neutral-900">
                    {titleCase(article.magnitude)}
                  </span>
                </span>
              )}
            </section>
          )}

          {/* Analysis */}
          {article.detailed_analysis && (
            <section>
              <SectionLabel>Analysis</SectionLabel>
              <div className="mt-2 space-y-3 text-sm leading-relaxed text-neutral-700">
                {article.detailed_analysis.split(/\n\s*\n/).map((para, i) => (
                  <p key={i}>{para.trim()}</p>
                ))}
              </div>
              {article.action_detail && (
                <div className="mt-3 rounded-md border-l-2 border-primary-300 bg-primary-50/40 px-3 py-2 text-sm text-neutral-800">
                  <span className="font-semibold text-primary-700">Recommended action: </span>
                  {article.action_detail}
                </div>
              )}
            </section>
          )}

          {/* Recommendations */}
          {article.recommendations.length > 0 && (
            <section>
              <SectionLabel>Recommendations</SectionLabel>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-neutral-700">
                {article.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-400"
                      aria-hidden
                    />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Keywords */}
          {hasKeywords && (
            <section>
              <SectionLabel>Keywords</SectionLabel>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[
                  ...article.related_commodities,
                  ...article.related_suppliers,
                  ...article.related_categories,
                ].map((kw, i) => (
                  <span
                    key={`${kw}-${i}`}
                    className="rounded border border-neutral-200 bg-white px-2 py-0.5 text-[11px] font-medium text-neutral-700"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </article>
  );
}

export const SourcingInsightCard = memo(SourcingInsightCardBase);
